
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Booking } from "@/hooks/useBookings";
import { Copy } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { copyUrlToClipboard } from "@/utils/urlUtils";
import { parseDateFromDatabase } from "@/utils/dateParser";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import React from "react";

// FUNCIÓN CORREGIDA: Usar parseDateFromDatabase para respetar fechas exactas
function formatFechaES(fechaISO: string): string {
  const date = parseDateFromDatabase(fechaISO);
  if (!date) return fechaISO;
  
  return format(date, 'dd/MMM/yyyy', { locale: es });
}

interface ReservationDetailModalProps {
  booking: Booking;
  isOpen: boolean;
  onClose: () => void;
  onCancelBooking: (bookingId: string) => void;
  isCanceling?: boolean;
}

const statusColors: Record<string, string> = {
  "Reservada": "bg-green-100 text-green-800",
  "Cancelada": "bg-red-100 text-red-800",
};

const ReservationDetailModal = ({
  booking,
  isOpen,
  onClose,
  onCancelBooking,
  isCanceling,
}: ReservationDetailModalProps) => {
  const { toast } = useToast();

  if (!isOpen) return null;

  const welcomeUrl = `${window.location.origin}/${booking.id}`;

  const handleCopy = async () => {
    const result = await copyUrlToClipboard(welcomeUrl);
    toast({
      description: result
        ? "¡Enlace copiado al portapapeles!"
        : "Error al copiar el enlace",
      variant: result ? "default" : "destructive",
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="p-0 max-w-md">
        <DialogHeader className="bg-gray-50 px-6 py-4 rounded-t-lg">
          <DialogTitle className="text-lg font-bold text-gray-800">
            Reservación de <span className="text-teal-700">{booking.guest_name}</span>
          </DialogTitle>
          <DialogDescription>
            <span
              className={`inline-block px-3 py-1 mt-3 rounded-full text-xs font-semibold ${statusColors[booking.status] || "bg-gray-100 text-gray-800"}`}
            >
              {booking.status}
            </span>
          </DialogDescription>
        </DialogHeader>
        <div className="px-6 py-4 space-y-4 bg-white">
          {/* Datos del huésped, propiedad y origen */}
          <div className="bg-gray-50 p-4 rounded-lg shadow-sm border space-y-2">
            <p className="font-medium text-gray-700">
              <span className="font-semibold">Huésped:</span> {booking.guest_name}
            </p>
            {booking.guest_email && (
              <p className="text-gray-600"><span className="font-semibold">Email:</span> {booking.guest_email}</p>
            )}
            {booking.guest_phone && (
              <p className="text-gray-600"><span className="font-semibold">Teléfono:</span> {booking.guest_phone}</p>
            )}
            {booking.origins?.name && (
              <p className="text-gray-600"><span className="font-semibold">Origen:</span> {booking.origins.name}</p>
            )}
          </div>
          <div className="bg-gray-50 p-4 rounded-lg shadow-sm border space-y-2">
            <p className="font-medium text-gray-700"><span className="font-semibold">Propiedad:</span> {booking.property_short_name || booking.property_id}</p>
            <div className="flex flex-wrap gap-3 mt-1">
              <span className="text-gray-600">
                <span className="font-semibold">Check-in:</span> {formatFechaES(booking.check_in)}
              </span>
              <span className="text-gray-600">
                <span className="font-semibold">Check-out:</span> {formatFechaES(booking.check_out)}
              </span>
              <span className="text-gray-600">
                <span className="font-semibold">Noches:</span> {booking.nights}
              </span>
            </div>
          </div>

          {/* Sección de Notas */}
          {booking.note && booking.note.trim().length > 0 && (
            <div className="bg-yellow-50 p-4 rounded-lg shadow-sm border border-yellow-200">
              <p className="font-semibold text-gray-700 mb-2">Notas:</p>
              <p className="text-gray-600 whitespace-pre-wrap">{booking.note}</p>
            </div>
          )}

          <div className="bg-gray-50 p-4 rounded-lg shadow-sm border flex flex-col gap-2">
            <Button
              variant="outline"
              size="sm"
              type="button"
              className="w-fit mx-auto flex items-center"
              onClick={handleCopy}
            >
              <Copy className="w-4 h-4 mr-2" />
              Copiar enlace de bienvenida
            </Button>
            
          </div>
        </div>
        <DialogFooter className="bg-gray-50 px-6 py-4 rounded-b-lg flex flex-col sm:flex-row gap-3">
          <Button
            variant="destructive"
            className="flex-1"
            onClick={() => onCancelBooking(booking.id)}
            disabled={isCanceling || booking.status === "Cancelada"}
          >
            {isCanceling ? "Cancelando..." : "Cancelar reservación"}
          </Button>
          <DialogClose asChild>
            <Button variant="outline" className="flex-1">
              Cerrar
            </Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ReservationDetailModal;
